import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState } from "react";
import { useStore } from "@/lib/store";
import { translations } from "@/lib/i18n";

export default function Header() {
  const { lang, setLang, cartCount, setCartOpen } = useStore();
  const t = translations[lang];
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { to: "/", label: t.nav_home },
    { to: "/shop", label: t.nav_shop },
    { to: "/lookbook", label: t.nav_lookbook },
    { to: "/about", label: t.nav_about },
    { to: "/contact", label: t.nav_contact },
  ];

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md border-b border-yellow-600/20"
      style={{ background: "rgba(5,5,5,0.78)" }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="header-logo-link"
          onClick={() => setMobileOpen(false)}
        >
          <img
            src="/logos/braf_zak_logo_main.jpeg"
            alt="Braf_Zak"
            className="w-11 h-11 rounded-full object-cover border border-yellow-500/40"
            style={{ boxShadow: "0 0 18px rgba(212,175,55,0.35)" }}
          />
          <span
            className="text-lg tracking-[0.3em] hidden sm:block"
            style={{
              fontFamily: '"Bebas Neue", "Oswald", system-ui',
              color: "#E8C766",
              textShadow: "0 2px 8px rgba(212,175,55,0.4)",
            }}
          >
            BRAF&nbsp;ZAK
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={`nav-${it.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-[0.18em] uppercase transition-colors duration-200 ${
                  isActive
                    ? "text-yellow-400"
                    : "text-white/75 hover:text-yellow-300"
                }`
              }
              style={{ fontFamily: '"Oswald", system-ui' }}
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="flex items-center text-xs font-bold rounded-full overflow-hidden border border-yellow-500/30"
            data-testid="lang-switcher"
          >
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-2.5 py-1 transition-colors ${
                lang === "fr"
                  ? "bg-yellow-500 text-black"
                  : "text-yellow-300 hover:bg-yellow-500/10"
              }`}
            >
              FR
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 transition-colors ${
                lang === "en"
                  ? "bg-yellow-500 text-black"
                  : "text-yellow-300 hover:bg-yellow-500/10"
              }`}
            >
              EN
            </button>
          </div>

          <button
            onClick={() => setCartOpen(true)}
            data-testid="cart-toggle-btn"
            className="relative p-2 rounded-full border border-yellow-500/30 hover:border-yellow-400 transition-colors"
          >
            <ShoppingBag className="w-5 h-5 text-yellow-300" />
            {cartCount > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                  color: "#1a0f00",
                }}
              >
                {cartCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 rounded-full border border-yellow-500/30 text-yellow-300"
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-yellow-600/20 bg-black/95 px-4 py-3 space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              onClick={() => setMobileOpen(false)}
              data-testid={`mobile-nav-${it.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `block px-3 py-3 text-sm tracking-[0.18em] uppercase rounded-md ${
                  isActive
                    ? "text-yellow-400 bg-yellow-500/10"
                    : "text-white/75 hover:bg-yellow-500/5"
                }`
              }
              style={{ fontFamily: '"Oswald", system-ui' }}
            >
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
